/**
 * render-add-items — 품목 추가 모달(순수 HTML). 구성 머리의 "＋ 항목" · 하단바 "＋ 항목 추가" · Ctrl+K 로 연다.
 *   ① 추가 위치(서버 칩 — 파트 견적이면 없음) → ② 카탈로그 검색(타이핑 즉시) → ③ 바구니에 담기(여러 개) → ④ 완료.
 *   완료 시 바구니가 그대로 POST /quote/:id/items 의 body 가 된다(뷰가 보냄). 소스 연결은 추가 뒤 지면에서.
 *   (설계 §3.5 · 승인 목업4를 우리 어휘로)
 */
import type { CatalogSearchHit } from '../../api/catalog-api';

const esc = (value: unknown) => String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const dollars = (amount: number | null | undefined) => (amount == null ? '' : ` · $${Math.round(amount).toLocaleString('en-US')}`);

/** 아직 id 없는 새 견적 항목 — 바구니 한 줄. category 는 견적 어휘(CPU·RAM…)로 정규화된 값(순서 규칙이 먹게). */
export interface NewItem {
  optionPart: string;
  description: string | null;
  category: string | null;
  quantity: number;
}

/** 품목 추가 모달 살림 — View 의 this.data.addItems 그대로(null 이면 닫힘). */
export interface AddItemsState {
  targetServerId: number | null;         // 담은 항목을 붙일 서버(quote_item id) — null = 개별 파트(서버 밖 · 파트 견적은 항상 null)
  query: string;                         // 검색 입력줄 문구 — 늦게 온 응답을 버리는 열쇠
  hits: CatalogSearchHit[] | null;       // null = 아직 검색 안 함 · [] = 없음
  isSearching: boolean;
  basket: NewItem[];                     // 담아둔 새 항목들 — 완료 때 한 번에 저장
}

/** 서버 칩 하나 — 서버 견적의 구성 머리들. 파트 견적이면 빈 배열(칩 줄 자체가 없다). */
export interface ServerChip { id: number; label: string }

const HITS_SHOWN = 20;

function renderTargetChips(state: AddItemsState, servers: ServerChip[]): string {
  if (servers.length === 0) return '';
  const chip = (id: number | null, label: string) =>
    `<button class="qa-chip${state.targetServerId === id ? ' on' : ''}" type="button" data-action="qe-add-target" data-server-id="${id ?? ''}">${esc(label)}</button>`;
  return `<div class="qa-loc"><span class="qa-loc-k">추가 위치</span>
    <div class="qa-chips">${servers.map((server) => chip(server.id, server.label)).join('')}${chip(null, '개별 파트')}</div></div>`;
}

function renderHits(state: AddItemsState): string {
  if (state.hits == null) return `<div class="qa-empty">파트넘버나 이름으로 검색해 품목을 담으세요.<br><span class="qa-dim">예: P408 · SSD · Xeon</span></div>`;
  if (state.hits.length === 0) return `<div class="qa-empty">‘${esc(state.query)}’ 검색 결과가 없습니다.</div>`;
  const rows = state.hits.slice(0, HITS_SHOWN).map((hit) => {
    const isInBasket = state.basket.some((item) => item.optionPart === hit.option_part);
    const stock = hit.qty_available != null ? `재고 ${hit.qty_available}` : '재고 —';
    return `<div class="qa-hit">
      <div class="qa-pn">${esc(hit.option_part)}</div>
      <div class="qa-desc">${esc(hit.description_raw ?? '')}</div>
      <div class="qa-stock">${stock}${dollars(hit.stock_cost_usd)}</div>
      <button class="qa-put${isInBasket ? ' done' : ''}" type="button" data-action="qe-add-to-basket"
        data-part="${esc(hit.option_part)}" data-desc="${esc(hit.description_raw ?? '')}" data-source="${esc(hit.source)}"${isInBasket ? ' disabled' : ''}>${isInBasket ? '담김' : '＋ 담기'}</button>
    </div>`;
  }).join('');
  return `<div class="qa-hits">${rows}</div>`;
}

function renderBasket(state: AddItemsState, targetLabel: string): string {
  if (state.basket.length === 0) return '';
  const rows = state.basket.map((item) => `<div class="qa-brow">
      <div class="qa-pn">${esc(item.optionPart)}</div>
      <div class="qa-desc">${esc(item.description ?? '')}</div>
      <div class="qa-qty"><button class="qa-step" type="button" data-action="qe-add-quantity" data-part="${esc(item.optionPart)}" data-delta="-1">−</button><span>${item.quantity}</span><button class="qa-step" type="button" data-action="qe-add-quantity" data-part="${esc(item.optionPart)}" data-delta="1">＋</button></div>
      <button class="qa-remove" type="button" data-action="qe-add-remove-from-basket" data-part="${esc(item.optionPart)}" title="바구니에서 빼기">✕</button>
    </div>`).join('');
  return `<div class="qa-basket">
    <div class="qa-basket-h">바구니 <span class="qa-cnt">${state.basket.length}</span>${targetLabel ? ` <span class="qa-to">→ ${esc(targetLabel)}</span>` : ''}</div>${rows}</div>`;
}

/** 품목 추가 모달 — 머리 · 추가 위치 칩 · 검색 입력줄 · 결과 · 바구니 · 바닥(완료). */
export function renderAddItemsModal(state: AddItemsState, servers: ServerChip[]): string {
  const targetLabel = servers.find((server) => server.id === state.targetServerId)?.label ?? (servers.length ? '개별 파트' : '');
  const canConfirm = state.basket.length > 0;
  return `<div class="qa-back" data-action="qe-add-close"></div>
  <div class="qa-modal" role="dialog">
    <div class="qa-h"><span>품목 추가</span><span class="qa-kbd">Ctrl K</span></div>
    ${renderTargetChips(state, servers)}
    <div class="qa-search"><span>🔎</span>
      <input class="qa-input" type="text" data-action="qe-add-query" placeholder="파트넘버·이름으로 검색 (입력하면 바로 검색)" value="${esc(state.query)}">
      ${state.isSearching ? '<span class="qa-spin">검색 중…</span>' : ''}</div>
    ${renderHits(state)}
    ${renderBasket(state, targetLabel)}
    <div class="qa-foot"><span class="qa-hint">입력하면 바로 검색 · 담기 후 완료 · Esc 닫기</span>
      <span><button class="qa-close" type="button" data-action="qe-add-close">닫기</button>
      <button class="qa-confirm${canConfirm ? '' : ' off'}" type="button" data-action="qe-add-confirm"${canConfirm ? '' : ' disabled'}>완료${canConfirm ? ` · ${state.basket.length}개 추가` : ''}</button></span>
    </div>
  </div>`;
}

export const ADD_ITEMS_CSS = `
.qa-back{position:fixed;inset:0;background:rgba(16,25,35,.32);z-index:60}
.qa-modal{position:fixed;left:50%;top:70px;transform:translateX(-50%);width:640px;max-height:82vh;overflow:auto;background:#fff;border:1px solid #cfd8e0;border-radius:14px;box-shadow:0 24px 64px rgba(10,25,35,.36);z-index:61;font:13px 'Malgun Gothic','맑은 고딕',system-ui,sans-serif}
.qa-h{display:flex;justify-content:space-between;align-items:center;padding:14px 18px;border-bottom:1px solid #eef1f4;font:700 15px system-ui;color:#16212b;position:sticky;top:0;background:#fff}
.qa-kbd{font:600 11px system-ui;color:#9aa7b1;border:1px solid #e0e6ec;border-radius:5px;padding:2px 7px}
.qa-loc{display:flex;align-items:center;gap:12px;padding:11px 18px;background:#fafbfc;border-bottom:1px solid #eef1f4}
.qa-loc-k{font-size:12px;color:#8a97a1;flex:0 0 auto}
.qa-chips{display:flex;gap:6px;flex-wrap:wrap}
.qa-chip{font:600 12px system-ui;padding:6px 12px;border:1px solid #d8e0ea;border-radius:7px;background:#fff;color:#5a6771;cursor:pointer}
.qa-chip.on{border-color:#2f6fed;background:#eef4ff;color:#2f6fed}
.qa-search{display:flex;align-items:center;gap:8px;margin:13px 18px;padding:9px 12px;border:1px solid #dbe2e8;border-radius:9px;background:#fafbfc}
.qa-search input{border:0;background:none;outline:none;flex:1;font:15px system-ui;color:#16212b}
.qa-spin{font-size:11.5px;color:#9aa7b1;flex:0 0 auto}
.qa-hits{border-top:1px solid #f0f3f6}
.qa-hit,.qa-brow{display:grid;grid-template-columns:130px 1fr auto auto;align-items:center;gap:12px;padding:9px 18px;border-bottom:1px solid #f4f6f8}
.qa-hit:hover{background:#f6f9fc}
.qa-pn{font:600 13px Consolas,monospace;color:#1f3a63;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.qa-desc{color:#3a4650;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.qa-stock{font-size:11.5px;color:#95a0a9;white-space:nowrap}
.qa-put{font:600 11px system-ui;background:#fff;border:1px solid #cdd8ea;border-radius:6px;padding:6px 11px;color:#2f6fed;cursor:pointer;white-space:nowrap}
.qa-put:hover{background:#eef4ff}.qa-put.done{color:#1a7d4d;border-color:#bfe3cd;background:#eaf5ee;cursor:default}
.qa-empty{padding:26px 18px;text-align:center;color:#95a0a9;line-height:1.6}.qa-dim{color:#b3bcc4;font-size:11.5px}
.qa-basket{margin:6px 0 0;border-top:6px solid #f0f3f6}
.qa-basket-h{padding:10px 18px 4px;font-size:12px;font-weight:700;color:#5a6771}
.qa-cnt{display:inline-block;min-width:18px;text-align:center;background:#2f6fed;color:#fff;border-radius:10px;padding:1px 7px;font-size:11px;margin:0 3px}
.qa-to{color:#8a97a1;font-weight:400}
.qa-brow{padding:7px 18px}
.qa-qty{display:flex;align-items:center;gap:8px;font-weight:700;color:#26323b}
.qa-step{width:22px;height:22px;border:1px solid #d8e0ea;border-radius:5px;background:#fff;color:#2f6fed;font-size:13px;cursor:pointer;line-height:1}
.qa-remove{border:0;background:none;color:#c0554b;cursor:pointer;font-size:13px}
.qa-foot{display:flex;justify-content:space-between;align-items:center;padding:12px 18px;border-top:1px solid #eef1f4;background:#fafbfc;position:sticky;bottom:0}
.qa-hint{font-size:11.5px;color:#aab4bd}
.qa-close{font:600 12px system-ui;background:#fff;border:1px solid #dbe2e8;border-radius:7px;padding:8px 14px;cursor:pointer;color:#5a6771;margin-right:8px}
.qa-confirm{font:700 13px system-ui;background:#2f6fed;color:#fff;border:0;border-radius:8px;padding:9px 18px;cursor:pointer}
.qa-confirm.off{background:#c8d2dd;cursor:default}
`;
